import { Flex, Box, Center, Spinner, Switch, Image } from '@chakra-ui/react';
import scorecardIcon from '@/assets/svg/scorecard/scorecard-icon.svg';
import { KText } from '@/ui-library/components';
import { AddQuestion } from './AddQuestion';
import { getScorecardInitialValues } from '../helpers';
import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { API_SERVICE } from '../../../../../api/api';
import { FormBuilder } from '@/components/formBuilder';
import qs from 'qs';
import { useInterviewStageManager } from '../../../../stage-manager';
import { ScoreCardBuilder } from '@/components/score-card-builder/ScoreCardBuilder';
import { ScoreCardFillWithAi } from '../ScoreCardFillWithAi';
import { useScorecardVisibilitySettings } from '@/api/scorecard-visibility';
import { useKFlags } from '@/hooks/use-k-flags';

interface ScoreCardData {
  feedback_description?: string | null;
  scorecard_feedback_description?: string | null;
  is_feedback_mandatory?: boolean | null;
  rating_description?: string | null;
  scorecard_rating_description?: string | null;
}

export const ScoreCard = ({
  formRef = null,
  currentTab = '',
  location = null,
  fill_with_ai_enabled = false,
  note_taker_enabled = false,
  onFillWithAiChange = null,
  scorecardData = {},
  observFunc = () => {},
  questions = [],
  note = 'This is the recommended scorecard template. You can customize it but changes will only apply to this interview.',
  wrapperProps = {},
  defaultScorecardParams = {},
  onTemplateSelect = (_) => {},
  scorecardRequired = true,
  onScorecardRequiredChange = null,
  toggleLabel = 'Include scorecards in this assessment',
}) => {
  const { activityAction } = useInterviewStageManager();
  const { feScorecardOptional } = useKFlags();
  const currentScorecardData = scorecardData as ScoreCardData;
  const { data: scorecardVisibilityData } = useScorecardVisibilitySettings();
  const showFillWithAi =
    scorecardVisibilityData?.data?.account_setting?.fill_with_ai_enabled;
  const scoreCardIntialValue = getScorecardInitialValues(questions);
  const [initialValues, setInitialValues] = useState({
    scoreCards: scoreCardIntialValue,
    feedback_description:
      currentScorecardData?.feedback_description ||
      currentScorecardData?.scorecard_feedback_description,
    is_feedback_mandatory: currentScorecardData?.is_feedback_mandatory ?? true,
    rating_description:
      currentScorecardData?.rating_description ||
      currentScorecardData?.scorecard_rating_description,
  });

  const [changeTemplateInProgress, setChangeTemplateInProgress] =
    useState(false);

  const templateParams = useMemo(() => {
    if (defaultScorecardParams) {
      const paramString = qs.stringify(defaultScorecardParams, {
        arrayFormat: 'brackets',
      });
      return paramString;
    }
  }, [defaultScorecardParams]);

  const { data: scorecardTemplates = [], isLoading } = useQuery({
    queryKey: [`ats_scorecards/list_templates/${templateParams}`],
    queryFn: () => API_SERVICE.getScoreCardTemplates(templateParams),
    staleTime: 1 * 60 * 1000, // 1 minute
    enabled: Object.keys(defaultScorecardParams).length > 0, // Only run query when defaultScorecardParams exists
  });

  const templateOptions = useMemo(
    () =>
      (scorecardTemplates ?? []).map((template) => ({
        value: template?.name,
        label: template?.name,
        questions: template?.questions,
        feedback_description: template?.feedback_description,
        rating_description: template?.rating_description,
        is_feedback_mandatory:
          template?.scorecard_config?.is_feedback_mandatory ??
          template?.is_feedback_mandatory ??
          false,
      })),
    [scorecardTemplates]
  );

  const handleTemplateOnChange = (selectedTemplate) => {
    setChangeTemplateInProgress(true);
    const scoreCardIntialValue = getScorecardInitialValues(
      selectedTemplate?.questions || []
    );
    setInitialValues({
      scoreCards: scoreCardIntialValue,
      feedback_description: selectedTemplate?.feedback_description,
      is_feedback_mandatory: selectedTemplate?.is_feedback_mandatory ?? false,
      rating_description: selectedTemplate?.rating_description,
    });
    setTimeout(() => {
      setChangeTemplateInProgress(false);
    }, 100);
  };

  const templatesFormConfig = [
    {
      name: 'scorecard_template',
      label: 'Scorecard template',
      type: 'select',
      isAppendToBody: true,
      placeholder: 'Select scorecard template',
      options: templateOptions,
      customOnChange: (selectedTemplate) => {
        onTemplateSelect?.(selectedTemplate);
        handleTemplateOnChange(selectedTemplate);
      },
    },
  ];

  useEffect(() => {
    if (
      scorecardTemplates?.length > 0 &&
      questions?.length === 0 &&
      activityAction?.type === 'new'
    ) {
      const defaultItem = {
        value: scorecardTemplates[0].name,
        label: scorecardTemplates[0].name,
        questions: scorecardTemplates[0].questions,
        feedback_description: scorecardTemplates[0].feedback_description,
        is_feedback_mandatory:
          scorecardTemplates[0].scorecard_config?.is_feedback_mandatory ??
          scorecardTemplates[0].is_feedback_mandatory ??
          true,
        rating_description: scorecardTemplates[0].rating_description,
      };
      // Defer so setState is not called synchronously in the effect
      const id = setTimeout(() => handleTemplateOnChange(defaultItem), 0);
      return () => clearTimeout(id);
    }
    // eslint-disable-next-line
  }, [scorecardTemplates]);

  return (
    <Flex p="16px 24px" flexDir="column" {...wrapperProps}>
      {onScorecardRequiredChange && feScorecardOptional && (
        <Flex
          alignItems="center"
          justifyContent="space-between"
          p="16px"
          bg="secondary-25"
          borderRadius="6px"
          mb={scorecardRequired ? '12px' : '0'}
        >
          <Flex alignItems="center" gap="12px">
            <Image
              src={scorecardIcon}
              alt=""
              width="35px"
              height="45px"
              flexShrink={0}
            />
            <KText size="sm-semi-bold" color="secondary-900">
              {toggleLabel}
            </KText>
          </Flex>
          <Switch
            className={`ats-switch-toogle ${scorecardRequired ? 'active' : ''}`}
            colorScheme="ats-primary"
            isChecked={scorecardRequired}
            onChange={(e) => onScorecardRequiredChange(e.target.checked)}
          />
        </Flex>
      )}
      <Flex
        flexDir="column"
        gap="12px"
        h={scorecardRequired ? 'auto' : '0'}
        overflow={scorecardRequired ? 'visible' : 'hidden'}
      >
        {isLoading && (
          <Center h="50vh" flex="1">
            <Spinner />
          </Center>
        )}
        {!isLoading && (
          <>
            {showFillWithAi && onFillWithAiChange && (
              <ScoreCardFillWithAi
                currentTab={currentTab}
                showFillWithAi={showFillWithAi}
                location={location}
                fill_with_ai_enabled={fill_with_ai_enabled}
                note_taker_enabled={note_taker_enabled}
                onFillWithAiChange={onFillWithAiChange}
              />
            )}
            {scorecardTemplates?.length > 0 && (
              <Box pos={'relative'}>
                <FormBuilder formRef={formRef} config={templatesFormConfig} />
              </Box>
            )}
            <Flex
              alignItems="center"
              p="15px"
              background={note ? 'ats-bg-primary' : 'transparent'}
              borderRadius="6px"
              gap="12px"
              justifyContent="center"
            >
              {note && (
                <KText as="span">
                  <b>Note:</b> {note}
                </KText>
              )}
              <AddQuestion formRef={formRef} />
            </Flex>
            <Box>
              {changeTemplateInProgress && (
                <Center h="50vh" flex="1">
                  <Spinner />
                </Center>
              )}
              {!changeTemplateInProgress && (
                <>
                  <ScoreCardBuilder
                    formRef={formRef}
                    initialValues={initialValues}
                    observFunc={observFunc}
                  />
                </>
              )}
            </Box>
          </>
        )}
      </Flex>
    </Flex>
  );
};
